/// <amd-module name="VSSUI/Components/ContextualMenuButton/ContextualMenuButton" />

import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import { IPoint } from "OfficeFabric/Utilities";
import * as React from "react";
import { getId } from "VSS/Core/Util/Object";
import "VSS/LoaderPlugins/Css!VSSUI/Components/ContextualMenuButton/ContextualMenuButton";
import { Button, IButton, IButtonProps } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { css } from "VSSUI/Util";
import { Location } from "VSSUI/Utilities/Position";
import { IContextualMenuButtonProps } from "../../Components/ContextualMenuButton/ContextualMenuButton.Props";
import { IVssContextualMenuItemProvider, VssContextualMenu } from "../../VssContextualMenu";
import { VssIcon } from "../../VssIcon";

/** @deprecated */
export interface IContextualMenuButtonState {
    providers: IVssContextualMenuItemProvider[] | undefined;
    items: IContextualMenuItem[] | undefined;
    isOpen: boolean;
    target?: HTMLElement | string | MouseEvent | IPoint | null;
    useTargetPoint?: boolean;
}

/**
 * Component that contains a button which, when activated, will show a contextual menu with the items provided
 */
/** @deprecated import { Menu } from "VSSUI/Menu" */
export class ContextualMenuButton extends React.Component<IContextualMenuButtonProps, IContextualMenuButtonState> {
    public static readonly ButtonContainerClassName: string = "vss-ContextualMenu--container";

    private static buttonRef: string = "button";
    private _contextMenuExpandedId: string;
    private _isMounted: boolean;
    private _button: IButton;

    constructor(props: IContextualMenuButtonProps) {
        super(props);

        if (this.props.isOpen) {
            this.state = {
                isOpen: true,
                providers: this.props.getItemProviders ? this.props.getItemProviders() : [],
                items: this.props.getItems ? this.props.getItems() : []
            };
        } else {
            this.state = { items: undefined, providers: undefined, isOpen: false };
        }
        this._contextMenuExpandedId = getId("context-menu-expanded");
    }

    public focus() {
        if (this._button && this._button.focus) {
            this._button.focus();
        }
    }

    public componentWillReceiveProps(nextProps: IContextualMenuButtonProps): void {
        if (nextProps.isOpen && !this.state.items) {
            this.setState({
                items: this.props.getItems ? this.props.getItems() : [],
                providers: this.props.getItemProviders ? this.props.getItemProviders() : []
            } as IContextualMenuButtonState);
        }
    }

    public componentDidMount(): void {
        this._isMounted = true;
    }

    public componentWillUnmount(): void {
        this._isMounted = false;
    }

    public render(): JSX.Element {
        const { className, ctaButton, directionalHint, iconProps, title, allocateSpaceWhileHidden, dataIndex, onFocus, tabIndex } = this.props;
        const isOpen: boolean = this.state.isOpen || this.props.isOpen || false;
        const showTooltip: boolean = this.props.showTooltip && !isOpen;

        const target = this.state.target || this.props.target || this.props.targetPoint;
        let isTargetAPoint: boolean | undefined = this.state.useTargetPoint || this.props.useTargetPoint;
        if (isTargetAPoint === undefined) {
            // See target is a point
            isTargetAPoint = target && (target as IPoint).x !== undefined && (target as IPoint).y !== undefined;
        }

        const buttonProps: IButtonProps = {
            ariaControls: isOpen ? this._contextMenuExpandedId : null,
            ariaExpanded: isOpen,
            ariaHasPopup: true,
            ariaLabel: title,
            className: css(
                "vss-ContextualMenuButton",
                "contextual-menu-button", // back compat
                className,
                isOpen && "open",
                allocateSpaceWhileHidden && "space-allocated",
                !allocateSpaceWhileHidden && "space-unallocated"
            ),
            dataIndex: dataIndex,
            excludeTabStop: tabIndex === -1,
            onClick: this._onClick,
            onFocus: onFocus,
            primary: ctaButton
        };

        if (title && showTooltip) {
            buttonProps.tooltipProps = {
                anchorOrigin: { horizontal: Location.center, vertical: Location.end },
                showOnFocus: true,
                text: title,
                tooltipOrigin: { horizontal: Location.center, vertical: Location.start }
            };
        }

        let button = (
            <Button ref={(button: IButton) => (this._button = button)} {...buttonProps}>
                {iconProps && <Icon {...VssIcon.getIconProps(iconProps)} />}
            </Button>
        );

        return (
            <div className={ContextualMenuButton.ButtonContainerClassName} ref={ContextualMenuButton.buttonRef} onClick={ev => ev.stopPropagation()}>
                {button}
                {isOpen && this.state.items && (
                    <VssContextualMenu
                        providers={this.state.providers}
                        items={this.state.items}
                        onDismiss={this._onDismiss}
                        shouldFocusOnMount={true}
                        gapSpace={isTargetAPoint ? 5 : 0}
                        directionalHint={directionalHint}
                        target={target || (this.refs[ContextualMenuButton.buttonRef] as HTMLDivElement)}
                        ariaLabel={title}
                    />
                )}
            </div>
        );
    }

    private _onClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
        const { onClick, useTargetPoint } = this.props;

        if (!onClick || !onClick(event)) {
            event.stopPropagation();
        }

        if (!this.state.isOpen) {
            this.setState({
                items: this.props.getItems ? this.props.getItems() : []
            } as IContextualMenuButtonState);

            if (event.clientX && useTargetPoint) {
                this.setState({
                    isOpen: true,
                    providers: this.props.getItemProviders ? this.props.getItemProviders() : [],
                    useTargetPoint: true,
                    target: { x: event.clientX, y: event.clientY }
                } as IContextualMenuButtonState);
            } else {
                // if this is a keydown event, we don't want to use target point we want to use target element
                this.setState({
                    isOpen: true,
                    providers: this.props.getItemProviders ? this.props.getItemProviders() : [],
                    useTargetPoint: false
                } as IContextualMenuButtonState);
            }
        } else {
            this.close();
        }
    };

    private _onDismiss = (): void => {
        if (this._isMounted) {
            if (this.props.onDismiss) {
                this.props.onDismiss();
            }
            this.close();
        }
    };

    private close = (): void => {
        this.setState({ target: null, isOpen: false, useTargetPoint: false, items: undefined, providers: undefined }, () => this._button.focus());
    };
}
